import {
  buttonStyles,
  Inline,
  Text,
  InlineLoading,
  useNotify,
  useViewport,
} from '@treatwell/ui';
import clsx from 'clsx';
import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppDispatch, useAppSelector} from '../../hooks/store';
import {postRescheduledOrder} from '../../store/slices/reschedule/slice';
import {
  selectDays,
  selectShowMultiStaffSelection,
  selectProposedServices,
  selectSelectedDate,
  selectSelectedEmployee,
  selectSelectedStartTime,
} from '../../store/slices/selectors';
import {generateReschedulePostOrder} from './generateReschedulePostOrder';
import styles from './Submit.module.css';

type Props = {
  label: string;
  disabled?: boolean;
  isLoading?: boolean;
  shouldShowMultiStaffSelection?: boolean;
};

const Label = ({isLoading, label}: {isLoading?: boolean; label: string}) => {
  return (
    <Inline justify="center" space="md">
      {isLoading && <InlineLoading />}

      {!isLoading && (
        <Text className={styles.label} type="bodyHeavy">
          {label}
        </Text>
      )}
    </Inline>
  );
};

type SubmitButtonProps = Props & {
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

const SubmitButton = ({
  label,
  disabled,
  isLoading,
  onClick,
  shouldShowMultiStaffSelection,
}: SubmitButtonProps) => {
  const linkStyles = clsx(
    buttonStyles.button,
    buttonStyles.primary,
    buttonStyles.lg,
    styles.link,
    styles.fullWidth,
    {
      [buttonStyles.disabled]: disabled,
      [buttonStyles.onLoading]: isLoading,
      [styles.disabled]: disabled,
      [styles.fullWidth]: shouldShowMultiStaffSelection,
    },
  );

  return (
    <button
      disabled={disabled || isLoading}
      onClick={onClick}
      className={linkStyles}
      data-cy="SubmitButton"
    >
      <Label isLoading={isLoading} label={label} />
    </button>
  );
};

export const RescheduleOrderSubmitContainer = () => {
  const {t} = useTranslation();
  const isMobileOrTablet = useViewport({device: ['mobile', 'tablet']});
  const label = isMobileOrTablet
    ? t('reschedule.orderSubmitLabelFixed')
    : t('reschedule.order');
  const [isLoading, setIsLoading] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const proposedServices = useAppSelector(selectProposedServices);
  const days = useAppSelector(selectDays);
  const date = useAppSelector(selectSelectedDate);
  const time = useAppSelector(selectSelectedStartTime);
  const selectedEmployee = useAppSelector(selectSelectedEmployee);
  const dispatch = useAppDispatch();
  const {notifyError} = useNotify();
  const isAvailableDay = Boolean(days[date]?.times.length);
  const shouldShowMultiStaffSelection = useAppSelector(
    selectShowMultiStaffSelection,
  );
  const body = generateReschedulePostOrder({
    date,
    time,
    proposedServices,
    isAvailableDay,
    employeeId: selectedEmployee,
    shouldShowMultiStaffSelection,
  });

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!body) {
      return;
    }

    event.preventDefault();

    setIsLoading(true);
    setIsDisabled(true);

    dispatch(postRescheduledOrder({url: location.href, body}))
      .unwrap()
      .catch(() => {
        notifyError(t('error.header'));
        setIsLoading(false);
        setIsDisabled(false);
      });
  };

  return (
    <SubmitButton
      label={label}
      isLoading={isLoading}
      disabled={isDisabled || !isAvailableDay || !time}
      onClick={handleClick}
      shouldShowMultiStaffSelection={shouldShowMultiStaffSelection}
    />
  );
};
